import { useState, useCallback } from 'react';
import { clientPipelinesAPI } from '../services/api';
import { useToast } from './use-toast';

export const usePipelineDragDrop = (selectedPipeline, fetchPipelineClients) => {
  const [draggedClient, setDraggedClient] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [moveDialog, setMoveDialog] = useState(false);
  const [movingClient, setMovingClient] = useState(null);
  const [sourceStage, setSourceStage] = useState(null);
  const [targetStage, setTargetStage] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [checkedTasks, setCheckedTasks] = useState([]);
  const { toast } = useToast();

  const handleDragStart = useCallback((e, client, stageId) => {
    setDraggedClient({ client, stageId });
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverStage(null);
  }, []);

  const handleDrop = useCallback(async (e, targetStageId) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!draggedClient || draggedClient.stageId === targetStageId) {
      setDraggedClient(null);
      return;
    }

    const { client, stageId: sourceStageId } = draggedClient;
    const stages = selectedPipeline?.stages || [];
    const sourceIdx = stages.findIndex(s => s.id === sourceStageId);
    const targetIdx = stages.findIndex(s => s.id === targetStageId);
    
    // Moving forward - check for incomplete tasks
    if (targetIdx > sourceIdx) {
      const sourceStageData = stages[sourceIdx];
      const incompleteTasks = (sourceStageData.tasks || []).filter(task => {
        const completedTasks = client.completedStageTasks?.[sourceStageId] || [];
        return !completedTasks.includes(task.id);
      });

      if (incompleteTasks.length > 0) {
        setMovingClient(client);
        setSourceStage(sourceStageData);
        setTargetStage(stages[targetIdx]);
        setPendingTasks(incompleteTasks);
        setCheckedTasks([]);
        setMoveDialog(true);
        setDraggedClient(null);
        return;
      }
    }

    // Direct move (backward or no tasks)
    try {
      await clientPipelinesAPI.update(client.assignmentId, { stageId: targetStageId });
      toast({ title: 'Success', description: `Moved ${client.displayName} to ${stages[targetIdx].name}` });
      fetchPipelineClients();
    } catch (error) {
      console.error('Error moving client:', error);
      toast({ title: 'Error', description: 'Failed to move client', variant: 'destructive' });
    }

    setDraggedClient(null);
  }, [draggedClient, selectedPipeline, toast, fetchPipelineClients]);

  const toggleTask = useCallback((taskId) => {
    setCheckedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  const handleCompleteAndMove = useCallback(async () => {
    if (!movingClient || !sourceStage || !targetStage) return;

    if (checkedTasks.length < pendingTasks.length) {
      toast({ title: 'Warning', description: 'Please complete all tasks before moving', variant: 'destructive' });
      return;
    }

    try {
      // Complete all checked tasks
      for (const taskId of checkedTasks) {
        await clientPipelinesAPI.completeTask(movingClient.assignmentId, {
          stageId: sourceStage.id,
          taskId
        });
      }

      // Move to target stage
      await clientPipelinesAPI.update(movingClient.assignmentId, { stageId: targetStage.id });
      
      toast({ title: 'Success', description: `Completed tasks and moved ${movingClient.displayName} to ${targetStage.name}` });
      setMoveDialog(false);
      setMovingClient(null);
      setSourceStage(null);
      setTargetStage(null);
      setPendingTasks([]);
      setCheckedTasks([]);
      fetchPipelineClients();
    } catch (error) {
      console.error('Error completing tasks:', error);
      toast({ title: 'Error', description: 'Failed to complete tasks', variant: 'destructive' });
    }
  }, [movingClient, sourceStage, targetStage, checkedTasks, pendingTasks, toast, fetchPipelineClients]);

  const cancelMove = useCallback(() => {
    setMoveDialog(false);
    setMovingClient(null);
    setSourceStage(null);
    setTargetStage(null);
    setPendingTasks([]);
    setCheckedTasks([]);
  }, []);

  return {
    draggedClient,
    dragOverStage,
    moveDialog,
    movingClient,
    sourceStage,
    targetStage,
    pendingTasks,
    checkedTasks,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    toggleTask,
    handleCompleteAndMove,
    cancelMove
  };
};
